import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PortalStatus from '@/components/portal/PortalStatus';
import Panel from '@/components/ui/Panel';
import { shortDate } from '@/lib/format';
import { portalClaims } from '@/mocks/portal';

export default function PortalWarrantyPage() {
  const { t } = useTranslation();
  const open = portalClaims.filter((c) => c.status === 'Open').length;

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 border-b border-background-200 pb-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-[20px] font-semibold leading-6 text-foreground-950">{t('portal.warranty.title')}</h1>
          <p className="mt-1 max-w-3xl text-[12.5px] leading-5 text-foreground-500">{t('portal.warranty.subtitle')}</p>
        </div>
        <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700">
          <i className="ri-shield-check-line text-[13px]" />
          {t('portal.warranty.claimCount', { count: open })}
        </span>
      </div>

      <div className="hidden overflow-hidden rounded-lg border border-background-200 bg-background-50 lg:block">
        <div className="overflow-x-auto thin-scroll">
          <table className="w-full min-w-[820px] border-collapse text-[12px]">
            <thead>
              <tr className="border-b border-background-200 bg-background-100/80 text-left text-[10px] uppercase tracking-[0.08em] text-foreground-500">
                <th className="px-4 py-2.5 font-medium">{t('portal.warranty.colClaim')}</th>
                <th className="px-3 py-2.5 font-medium">{t('portal.warranty.colPo')}</th>
                <th className="px-3 py-2.5 font-medium">{t('portal.warranty.colItem')}</th>
                <th className="px-3 py-2.5 font-medium">{t('portal.warranty.colStatus')}</th>
                <th className="px-3 py-2.5 font-medium">{t('portal.warranty.colUpdated')}</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {portalClaims.map((c) => (
                <tr key={c.claim} className="border-b border-background-200/70 last:border-0 hover:bg-primary-50/30">
                  <td className="px-4 py-2.5">
                    <p className="tnum text-[12px] font-semibold text-foreground-950">{c.claim}</p>
                    <p className="text-[10px] text-foreground-400">
                      {c.photos.length} {t('portal.warranty.photos')}
                    </p>
                  </td>
                  <td className="tnum px-3 py-2.5 text-[11px] text-primary-700">{c.po}</td>
                  <td className="px-3 py-2.5">
                    <p className="tnum text-[11px] font-semibold text-foreground-800">{c.sku}</p>
                    <p className="max-w-[220px] truncate text-[11px] text-foreground-500">{c.item}</p>
                  </td>
                  <td className="px-3 py-2.5">
                    <PortalStatus status={c.status} />
                  </td>
                  <td className="tnum px-3 py-2.5 text-[11px] text-foreground-600">{shortDate(c.updated)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <Link
                      to={`/portal/warranty/${c.claim}`}
                      className="inline-flex h-7 items-center gap-1 whitespace-nowrap rounded border border-background-300 bg-background-50 px-2 text-[10.5px] font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                    >
                      {t('portal.warranty.open')}
                      <i className="ri-arrow-right-line text-[11px]" />
                    </Link>
                  </td>
                </tr>
              ))}
              {portalClaims.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-[12px] text-foreground-500">
                    {t('portal.warranty.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-col gap-2.5 lg:hidden">
        {portalClaims.map((c) => (
          <Link
            key={c.claim}
            to={`/portal/warranty/${c.claim}`}
            className="rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:bg-background-100/50"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="tnum text-[12px] font-semibold text-foreground-950">{c.claim}</p>
                <p className="tnum text-[10.5px] text-primary-700">{c.po}</p>
              </div>
              <PortalStatus status={c.status} className="!px-1.5 !text-[10px]" />
            </div>
            <p className="mt-2 truncate text-[11.5px] text-foreground-800">
              {c.sku} · {c.item}
            </p>
            <p className="mt-1 flex items-center gap-1 text-[10.5px] text-foreground-400">
              <i className="ri-image-line text-[11px]" />
              {c.photos.length} {t('portal.warranty.photos')} · {shortDate(c.updated)}
            </p>
          </Link>
        ))}
        {portalClaims.length === 0 && (
          <p className="rounded-lg border border-background-200 bg-background-50 px-3 py-8 text-center text-[12px] text-foreground-500">
            {t('portal.warranty.empty')}
          </p>
        )}
      </div>

      <Panel className="mt-4 !p-3">
        <p className="flex items-start gap-1.5 text-[10.5px] leading-4 text-foreground-500">
          <i className="ri-information-line mt-px text-[12px] text-foreground-400" />
          {t('portal.warranty.warrantyNote')}
        </p>
      </Panel>
    </div>
  );
}